import Modal from '..';
import * as S from './styles';
import { useMemo, useState } from 'react';
import { Style } from 'dragonmouse-shared/style';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import { Attachment } from 'dragonmouse-shared/interfaces';
import Icon, { IconType } from 'components/Icon';
import { useTheme } from 'styled-components';
import { CreateStyleRequest } from 'dragonmouse-shared/api/forge';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import { Loader } from 'components/Widget/Loader';
import { generateShortcutText, FileUploadCallbacks, uploadFile, UploadTracker } from 'utils';

interface CreateDsFromImageModalProps {
	close: () => void;
	onStyleCreated: (style: Style) => void;
}

const CreateDsFromImageModal = ({ close, onStyleCreated }: CreateDsFromImageModalProps) => {
	const [attachment, setAttachment] = useState<Attachment | null>(null);
	const [attachmentImageData, setAttachmentImageData] = useState<string | null>(null);

	const [loadingAttachment, setLoadingAttachment] = useState<UploadTracker | null>(null);
	const [loading, setLoading] = useState(false);
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const theme = useTheme();

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onCancel = () => {
		close();
	};

	const onSubmit = async () => {
		setLoading(true);
		if (!attachment) {
			toast.error('No image provided');
			setLoading(false);
			return;
		}
		const req: CreateStyleRequest = {
			attachments: [attachment],
		};
		const style = await forge.createStyle.withToast()(req);
		if (style) {
			onStyleCreated(style.style);
		} else {
			toast.error('Failed to create design system');
		}
		setLoading(false);
	};

	// File upload callbacks with functional setState
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				setLoadingAttachment(uploadTracker);
			},
			onLoadingEnd: (_uniqueId: string) => {
				setLoadingAttachment(null);
			},
			onSuccess: (attachment: Attachment) => {
				setAttachment(attachment);
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[]
	);

	// Handler that reads file for preview and then uploads using the utility
	const handleFileUpload = async (file: File) => {
		// Read file as data URL for image preview
		const previewReader = new FileReader();
		previewReader.onload = () => {
			setAttachmentImageData(previewReader.result as string);
		};
		previewReader.readAsDataURL(file);

		// Use the standard upload utility for actual upload
		await uploadFile(file, fileCallbacks);
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);
		e.preventDefault();
		e.stopPropagation();
		if (loadingAttachment) {
			toast.info('Upload in progress. Please wait.');
			return;
		}

		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			handleFileUpload(files[0]); // Only take first file for image modal
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const onChooseFile = () => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) await handleFileUpload(file);
		};
		input.click();
	};

	const renderDropZone = () => {
		if (loadingAttachment) {
			return (
				<S.DropZone onClick={onChooseFile}>
					<S.DropZoneLoading>
						<Loader width={24} height={24} />
						<S.DropZoneLoadingText>Uploading image...</S.DropZoneLoadingText>
					</S.DropZoneLoading>
				</S.DropZone>
			);
		} else if (attachment && attachmentImageData) {
			return <S.DropZone onClick={onChooseFile} backgroundImage={attachmentImageData}></S.DropZone>;
		}
		return (
			<S.DropZone onClick={onChooseFile}>
				<S.DropZoneTitle>
					Drop here or {generateShortcutText('v', { ctrl: true })} to paste
				</S.DropZoneTitle>
				<S.DropZoneSubtitle>
					Colors, typography, and design tokens will be extracted from your design.
				</S.DropZoneSubtitle>
				<Button type={ButtonType.PRIMARY}>Choose Image</Button>
			</S.DropZone>
		);
	};

	return (
		<>
			{loading ? (
				<LoadingOverlay
					text={'Creating design system... this usually takes about 20 seconds.'}
					semiTransparent
				/>
			) : (
				<Modal
					title={'Create design system from image'}
					cancelLabel="Cancel"
					submitLabel={'Create design system'}
					onCancel={onCancel}
					onSubmit={onSubmit}
					disabled={!attachment}
				>
					<S.Content
						onDragOver={onDragOver}
						onDragLeave={onDragLeave}
						onDrop={onDrop}
						onPaste={(e) => {
							const items = e.clipboardData?.items;
							if (!items) return;

							for (const item of items) {
								if (item.kind === 'file') {
									const file = item.getAsFile();
									if (file) {
										handleFileUpload(file);
										e.preventDefault();
										return;
									}
								}
							}
						}}
					>
						{renderDropZone()}
						{isDraggingFile && (
							<S.DragOverlay>
								<S.DragOverlayContent>
									<Icon
										type={IconType.ATTACHMENT}
										width={32}
										height={32}
										color={theme.gray[1000]}
									/>
									<S.DragOverlayText>Drop file to upload</S.DragOverlayText>
								</S.DragOverlayContent>
							</S.DragOverlay>
						)}
					</S.Content>
				</Modal>
			)}
		</>
	);
};

export default CreateDsFromImageModal;
